import {
  Badge,
  Box,
  Flex,
  Heading,
  Table,
  Text,
  VStack,
} from "@chakra-ui/react"
import { createFileRoute } from "@tanstack/react-router"
import { useRef, useState } from "react"
import { useTranslation } from "react-i18next"
import { FiAlertTriangle, FiDownload, FiUpload } from "react-icons/fi"

import { VisionButton, VisionCard } from "@/components/VisionUI"
import { Checkbox } from "@/components/ui/checkbox"
import useCustomToast from "@/hooks/useCustomToast"
import { useSemanticColors } from "@/hooks/useSemanticColors"

export const Route = createFileRoute("/_layout/admin-backup")({
  component: AdminBackup,
})

function AdminBackup() {
  const { t } = useTranslation("admin")
  const sc = useSemanticColors()
  const toast = useCustomToast()

  const [includeReadings, setIncludeReadings] = useState(false)
  const [isExporting, setIsExporting] = useState(false)
  const [isRestoring, setIsRestoring] = useState(false)
  const [selectedFile, setSelectedFile] = useState<File | null>(null)
  const [restoreStats, setRestoreStats] = useState<Record<
    string,
    number
  > | null>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)

  const handleExport = async () => {
    setIsExporting(true)
    try {
      const token = localStorage.getItem("access_token")
      const apiUrl = import.meta.env.VITE_API_URL || ""
      const res = await fetch(
        `${apiUrl}/api/v1/admin/backup/export?include_readings=${includeReadings}`,
        { headers: { Authorization: `Bearer ${token}` } },
      )
      if (!res.ok) {
        const err = await res.json().catch(() => ({ detail: res.statusText }))
        throw new Error(err.detail || res.statusText)
      }
      const blob = await res.blob()
      const disposition = res.headers.get("content-disposition") || ""
      const match = disposition.match(/filename="(.+)"/)
      const filename = match ? match[1] : "qdat_backup.zip"
      const link = document.createElement("a")
      link.href = URL.createObjectURL(blob)
      link.download = filename
      link.click()
      URL.revokeObjectURL(link.href)
      toast.showSuccessToast(t("backup.downloadBtn"))
    } catch (err) {
      toast.showErrorToast(String(err))
    } finally {
      setIsExporting(false)
    }
  }

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0] ?? null
    setSelectedFile(f)
    setRestoreStats(null)
  }

  const handleRestore = async () => {
    if (!selectedFile) return
    if (!window.confirm(t("backup.restoreConfirm"))) return

    setIsRestoring(true)
    setRestoreStats(null)
    try {
      const token = localStorage.getItem("access_token")
      const apiUrl = import.meta.env.VITE_API_URL || ""
      const form = new FormData()
      form.append("file", selectedFile)
      const res = await fetch(`${apiUrl}/api/v1/admin/backup/restore`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
        body: form,
      })
      if (!res.ok) {
        const err = await res.json().catch(() => ({ detail: res.statusText }))
        throw new Error(err.detail || res.statusText)
      }
      const data = await res.json()
      setRestoreStats(data.rows_restored ?? {})
      toast.showSuccessToast(t("backup.restoreSuccess"))
      setSelectedFile(null)
      if (fileInputRef.current) fileInputRef.current.value = ""
    } catch (err) {
      toast.showErrorToast(String(err))
    } finally {
      setIsRestoring(false)
    }
  }

  return (
    <Box>
      <Box mb={8}>
        <Heading size="xl" color={sc.heading} mb={2}>
          {t("backup.title")}
        </Heading>
        <Text color={sc.textMuted}>{t("backup.subtitle")}</Text>
      </Box>

      {/* Export section */}
      <VisionCard variant="elevated" mb={6}>
        <Box p={6}>
          <Heading size="md" color={sc.heading} mb={1}>
            {t("backup.exportTitle")}
          </Heading>
          <Text color={sc.textMuted} fontSize="sm" mb={5}>
            {t("backup.exportDesc")}
          </Text>

          <VStack align="stretch" gap={4}>
            <Checkbox
              checked={includeReadings}
              onCheckedChange={({ checked }: { checked: boolean }) =>
                setIncludeReadings(checked)
              }
            >
              <Box>
                <Text fontSize="sm" fontWeight="medium" color={sc.text}>
                  {t("backup.includeReadings")}
                </Text>
                <Text fontSize="xs" color={sc.textMuted}>
                  {t("backup.includeReadingsDesc")}
                </Text>
              </Box>
            </Checkbox>

            <Box>
              <VisionButton
                variant="brand"
                onClick={handleExport}
                loading={isExporting}
                disabled={isExporting}
              >
                <FiDownload />
                {t("backup.downloadBtn")}
              </VisionButton>
            </Box>
          </VStack>
        </Box>
      </VisionCard>

      {/* Restore section */}
      <VisionCard variant="elevated">
        <Box p={6}>
          <Heading size="md" color={sc.heading} mb={1}>
            {t("backup.restoreTitle")}
          </Heading>
          <Text color={sc.textMuted} fontSize="sm" mb={4}>
            {t("backup.restoreDesc")}
          </Text>

          {/* Warning banner */}
          <Flex
            align="flex-start"
            gap={3}
            p={4}
            mb={5}
            borderRadius="lg"
            bg="rgba(229,62,62,0.1)"
            border="1px solid rgba(229,62,62,0.3)"
          >
            <Box color="red.400" mt="2px" flexShrink={0}>
              <FiAlertTriangle />
            </Box>
            <Text fontSize="sm" color="red.300">
              {t("backup.restoreWarning")}
            </Text>
          </Flex>

          <VStack align="stretch" gap={4}>
            <Box>
              <Text fontSize="sm" color={sc.textMuted} mb={2}>
                {t("backup.selectFileTip")}
              </Text>
              <Flex align="center" gap={3} flexWrap="wrap">
                <Box
                  as="label"
                  cursor="pointer"
                  display="inline-flex"
                  alignItems="center"
                  gap={2}
                  px={4}
                  py={2}
                  borderRadius="lg"
                  border="1px solid"
                  borderColor={sc.border}
                  bg="rgba(255,255,255,0.04)"
                  _hover={{ bg: "rgba(255,255,255,0.08)" }}
                  fontSize="sm"
                  color={sc.text}
                >
                  <FiUpload />
                  {t("backup.chooseFile")}
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept=".zip"
                    onChange={handleFileChange}
                    style={{ display: "none" }}
                  />
                </Box>
                {selectedFile ? (
                  <Text fontSize="sm" color={sc.text}>
                    {selectedFile.name}
                  </Text>
                ) : (
                  <Text fontSize="sm" color={sc.textMuted}>
                    {t("backup.noFileSelected")}
                  </Text>
                )}
              </Flex>
            </Box>

            <Box>
              <VisionButton
                variant="ghost"
                onClick={handleRestore}
                loading={isRestoring}
                disabled={!selectedFile || isRestoring}
                style={{
                  borderColor: "rgba(229,62,62,0.5)",
                  color: "rgb(252,129,129)",
                }}
              >
                <FiUpload />
                {t("backup.restoreBtn")}
              </VisionButton>
            </Box>
          </VStack>

          {/* Restore results */}
          {restoreStats && (
            <Box mt={6}>
              <Text fontSize="sm" fontWeight="medium" color={sc.heading} mb={3}>
                {t("backup.rowsRestored")}
              </Text>
              <Table.Root size="sm" variant="outline">
                <Table.Body>
                  {Object.entries(restoreStats).map(([table, count]) => (
                    <Table.Row key={table}>
                      <Table.Cell>
                        <Text fontSize="sm" fontFamily="mono" color={sc.text}>
                          {table}
                        </Text>
                      </Table.Cell>
                      <Table.Cell textAlign="right">
                        <Badge colorPalette="green" variant="subtle">
                          {count}
                        </Badge>
                      </Table.Cell>
                    </Table.Row>
                  ))}
                </Table.Body>
              </Table.Root>
            </Box>
          )}
        </Box>
      </VisionCard>
    </Box>
  )
}
